import { clearNoteDrafts } from '../../../shared/project-reports/drafts.ts';
// ================================================================
// Auth — مدیریت نشست (login/register/refresh/logout) + گاردِ بوت
// ================================================================
// همون origin و الگوی fetch مثل core/oauth.js.
// توکن دسترسی زیر کلیدِ 'levelupwerk:token' ذخیره می‌شه (همونی که
// oauth.js می‌خونه) تا جریان OAuth گوگل بدون تغییر کار کنه.

import { apiRequest } from './apiClient.js';

const TOKEN_KEY = 'levelupwerk:token';
const REFRESH_KEY = 'levelupwerk:refresh';
const USER_KEY = 'levelupwerk:user';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

export function getUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function notifyLeavingSession(previous) {
  if (typeof window !== 'undefined' && previous?.id && previous?.organization?.id) {
    void clearNoteDrafts(`${previous.organization.id}:${previous.id}`).catch(() => {});
    window.dispatchEvent(new CustomEvent('auth:leaving-session', { detail: { owner: `${previous.organization.id}:${previous.id}` } }));
  }
}

export function setSession({ accessToken, refreshToken, user } = {}) {
  const previous = getUser();
  if (user && previous && (previous.id !== user.id || previous.organization?.id !== user.organization?.id)) notifyLeavingSession(previous);
  if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  notifyLeavingSession(getUser());
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  // Gemerkte Firmenfarbe (ORG_THEME_CACHE_KEY in core/orgTheme.js). Hier als
  // Text und nicht als Import: orgTheme.js hängt über core/org.js an DIESER
  // Datei — ein Import zurück wäre ein Ringschluss.
  localStorage.removeItem('lu.orgTheme');
}

// POST /api/auth/login → { user, accessToken, refreshToken } — noch kein Token vorhanden
export async function login(email, password) {
  const result = await apiRequest('/api/auth/login', {
    auth: false,
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  // Zweiter Faktor aktiv? Dann ist das hier NOCH KEINE Session — nur die
  // Aufforderung, den Code zu tippen. `setSession` darf hier NICHT laufen,
  // sonst wäre der Nutzer mit halbem Login «eingeloggt».
  if (result?.mfaRequired) return result;
  // Gleiches gilt für die Geräte-Rückfrage: das Konto ist schon auf einem
  // anderen Gerät angemeldet. Erst nach «ja, melde es ab» gibt es eine Session.
  if (result?.deviceConfirmRequired) return result;
  setSession(result);
  // کاربرِ کامل (organization + role + permissions) را بگیر و ذخیره کن
  try { await fetchMe(); } catch { /* نشست معتبره؛ کاربرِ کامل بعداً sync می‌شود */ }
  return result;
}

// Schritt 2 des Logins: Code (aus der App oder ein Backup-Code) + der
// kurzlebige mfaToken aus Schritt 1 → echte Session.
export async function verifyMfaLogin(mfaToken, code) {
  const result = await apiRequest('/api/auth/mfa/verify', {
    auth: false,
    method: 'POST',
    body: JSON.stringify({ mfaToken, code }),
  });
  // Auch NACH dem zweiten Faktor kann noch die Geräte-Rückfrage kommen: der
  // Code war richtig, aber das Konto läuft bereits auf einem anderen Gerät.
  if (result?.deviceConfirmRequired) return result;
  setSession(result);
  try { await fetchMe(); } catch { /* نشست معتبره */ }
  return result;
}

// ── «Ein Konto, ein Gerät» ────────────────────────────────

// POST /api/auth/login/confirm-device — Schritt 2: «ja, melde die anderen ab».
// Erst DIESER Aufruf wirft die anderen Geräte raus; wer abbricht, lässt sie
// in Ruhe. Antwort ist eine echte Session.
export async function confirmDeviceLogin(deviceToken, deviceName) {
  const payload = { deviceToken };
  if (deviceName) payload.deviceName = deviceName;
  const result = await apiRequest('/api/auth/login/confirm-device', {
    auth: false,
    method: 'POST',
    body: JSON.stringify(payload),
  });
  setSession(result);
  try { await fetchMe(); } catch { /* نشست معتبره */ }
  return result;
}

// GET /api/auth/devices → { devices, maxSessions }
export function fetchDevices() {
  return apiRequest('/api/auth/devices');
}

// DELETE /api/auth/devices/:id — dieses Gerät abmelden
export function signOutDevice(id) {
  return apiRequest('/api/auth/devices/' + encodeURIComponent(id), { method: 'DELETE' });
}

// PATCH /api/auth/devices/:id — Gerät umbenennen (leer = automatischer Name)
export function renameDevice(id, deviceName) {
  return apiRequest('/api/auth/devices/' + encodeURIComponent(id), {
    method: 'PATCH',
    body: JSON.stringify({ deviceName }),
  });
}

// ── Zwei-Faktor-Verwaltung (Einstellungen → Mein Konto) ──
export function getMfaStatus() {
  return apiRequest('/api/auth/mfa/status');
}

export function startMfaSetup() {
  return apiRequest('/api/auth/mfa/setup', { method: 'POST' });
}

export function enableMfa(code) {
  return apiRequest('/api/auth/mfa/enable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export function disableMfa(password, code) {
  return apiRequest('/api/auth/mfa/disable', {
    method: 'POST',
    body: JSON.stringify({ password, code }),
  });
}

// POST /api/auth/register → خودش توکن می‌ده، پس مستقیم وارد می‌شیم
export async function register(email, password, name) {
  const payload = { email, password };
  if (name) payload.name = name;
  const result = await apiRequest('/api/auth/register', {
    auth: false,
    method: 'POST',
    body: JSON.stringify(payload),
  });
  setSession(result);
  try { await fetchMe(); } catch { /* نشست معتبره؛ کاربرِ کامل بعداً sync می‌شود */ }
  return result;
}

// POST /api/auth/refresh (rotation) — توکن نو می‌ذاره. true=موفق
export async function tryRefresh({ throwOnTransient = false } = {}) {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  try {
    const result = await apiRequest('/api/auth/refresh', {
      auth: false,
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
    setSession(result);
    return true;
  } catch (err) {
    // A network failure, throttle or server outage does not revoke the session.
    if (throwOnTransient && err.status !== 401) throw err;
    return false;
  }
}

// GET /api/auth/me — اعتبارسنجی توکن فعلی
export async function fetchMe() {
  // Kurzschluss ohne Netzwerk-Roundtrip, wenn eh kein Token da ist.
  if (!getToken()) {
    throw Object.assign(new Error('No token'), { status: 401 });
  }
  const result = await apiRequest('/api/auth/me');
  if (result?.user) {
    localStorage.setItem(USER_KEY, JSON.stringify(result.user));
  }
  return result.user;
}

// POST /api/auth/logout — session رو باطل و storage رو پاک می‌کنه
export async function logout() {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await apiRequest('/api/auth/logout', {
        auth: false,
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // مهم نیست؛ به‌هرحال محلی پاک می‌کنیم
    }
  }
  clearSession();
}

// گاردِ بوت: true یعنی نشست معتبره و اپ می‌تونه بالا بیاد.
// apiRequest already performs the single shared refresh on an expired token.
export async function ensureAuthenticated() {
  if (!getToken()) return false;
  try {
    await fetchMe();
    return true;
  } catch (err) {
    if (err.status === 401) {
      clearSession();
      return false;
    }
    // Let bootstrap offer a retry; never show login for a temporary outage.
    throw err;
  }
}

// ── دعوت‌ها (Invitations) ──────────────────────────────────

// GET /api/invitations/token/:token → { email, organization, role }
export async function fetchInvite(token) {
  return apiRequest('/api/invitations/token/' + encodeURIComponent(token), { auth: false });
}

// POST /api/invitations/accept → { user, accessToken, refreshToken }
export async function acceptInvite(token, password, name) {
  const payload = { token, password };
  if (name) payload.name = name;
  const result = await apiRequest('/api/invitations/accept', {
    auth: false,
    method: 'POST',
    body: JSON.stringify(payload),
  });
  setSession(result);
  return result;
}

// ── Passwort vergessen ────────────────────────────────────

// POST /api/auth/forgot-password → { ok: true }
// Antwortet auch für unbekannte Adressen mit ok — die Oberfläche darf daraus
// KEINE Aussage über die Existenz des Kontos machen (siehe Backend-Service).
export async function requestPasswordReset(email) {
  return apiRequest('/api/auth/forgot-password', {
    auth: false,
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

// GET /api/auth/reset-password/:token → { email }
export async function fetchResetToken(token) {
  return apiRequest('/api/auth/reset-password/' + encodeURIComponent(token), { auth: false });
}

// POST /api/auth/reset-password → { user, accessToken, refreshToken }
// Der Server meldet direkt an (wie acceptInvite) und beendet dabei alle
// bisherigen Sessions.
export async function submitPasswordReset(token, password) {
  const result = await apiRequest('/api/auth/reset-password', {
    auth: false,
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
  setSession(result);
  return result;
}

// POST /api/auth/change-password → { user, accessToken, refreshToken }
// Passwort im eingeloggten Zustand ändern. Der Server beendet dabei ALLE
// Sessions (alle anderen Geräte sind abgemeldet) und gibt für DIESES Gerät
// sofort eine neue — darum setSession: ohne das wäre man selbst ausgesperrt,
// weil das alte Refresh-Token gerade mitgelöscht wurde.
export async function changePassword(currentPassword, newPassword) {
  const result = await apiRequest('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  setSession(result);
  return result;
}

// ── Einrichtungs-Assistent ──
// Sichtbar NUR, solange die Firma ihn nie abgeschlossen hat. Quelle ist der
// Server (organization.onboardingCompletedAt) — nicht localStorage: sonst
// käme er auf jedem neuen Gerät/Browser desselben Kunden wieder.
export function needsOnboarding() {
  return !getUser()?.organization?.onboardingCompletedAt;
}

/** Einmal pro Firma: Assistent als erledigt markieren + lokalen Cache nachziehen. */
export async function completeOnboarding() {
  const r = await apiRequest('/api/auth/onboarding-complete', { method: 'POST' });
  const u = getUser();
  if (u?.organization) {
    u.organization.onboardingCompletedAt = r?.onboardingCompletedAt || new Date().toISOString();
    localStorage.setItem(USER_KEY, JSON.stringify(u));
  }
  return r;
}

// ── مجوزها (از روی کاربرِ ذخیره‌شده) ──
export function getPermissions() {
  const u = getUser();
  return new Set(u?.role?.permissions || []);
}
export function hasPermission(perm) {
  return getPermissions().has(perm);
}

// ── ماژول‌های پلن سازمان (Entitlements) ──
// لیست خالی/غایب = همه‌ی ماژول‌ها فعال (Vollzugang).
// برای ماژول‌هایی که permission مخصوص ندارند (مثل projects) استفاده می‌شود؛
// بقیه از قبل در permissions اثر داده شده‌اند (backend: effectivePermissions).
export function isModuleEnabled(moduleKey) {
  if (getUser()?.organization?.isPlatform) return true;
  const mods = getUser()?.organization?.enabledModules;
  if (!Array.isArray(mods) || mods.length === 0) return true;
  return mods.includes(moduleKey);
}

export function hasProjectWorkspaceAccess() {
  return isModuleEnabled('projects') && hasPermission('customers:read');
}

/** Server rollout snapshot for new native modules; permissions still apply separately. */
export function isNativePlatformFeatureEnabled(featureKey) {
  const features = getUser()?.organization?.nativePlatformFeatures;
  return Object.hasOwn(features ?? {}, featureKey) && features[featureKey] === true;
}

/** Existing reads may be available during an explicitly configured recovery stop. */
export function isNativePlatformFeatureReadable(featureKey) {
  if (isNativePlatformFeatureEnabled(featureKey)) return true;
  if (!['universal_capture', 'financial_projection'].includes(featureKey)) return false;
  const features = getUser()?.organization?.nativePlatformReadFeatures;
  return Object.hasOwn(features ?? {}, featureKey) && features[featureKey] === true;
}
